import React, {useState} from 'react';
import {createPortal} from 'react-dom';
import {Link, useLocation} from 'react-router-dom';
import logo from '../images/translon-icon.png';
import SidebarImportMenu from './SidebarImportMenu';
import SidebarExportMenu from './SidebarExportMenu';
import SidebarCarburantMenu from './SidebarCarburantMenu';
import SidebarPannesMenu from './SidebarPannesMenu';
import SidebarReglagesMenu from './SidebarReglagesMenu';
import AlertesCloche from './AlertesCloche';
import {useLanguage} from '../context/LanguageContext';
import {hasRole} from '../utils/roles';

interface SidebarProps {
    user: any;
    onLogout: () => void;
    onToggle: (isCollapsed: boolean) => void;
    isCollapsed?: boolean;
    isMobileView?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({user, onLogout, onToggle, isCollapsed = false, isMobileView = false}) => {
    const location = useLocation();
    const {t} = useLanguage();
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const [flyoutPos, setFlyoutPos] = useState<{ top: number; left: number } | null>(null);
    const [menuSearch, setMenuSearch] = useState('');

    const normalize = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

    // isActive est défini plus bas, après menuItems : déterminer l'entrée active suppose de
    // connaître tous les chemins du menu (cf. la règle du chemin le plus long).

    const toggleSidebar = () => {
        const newCollapsedState = !isCollapsed;
        onToggle(newCollapsedState);
    };

    const menuItems = [
        // Récupérations est désormais dans le sous-menu "Imports"
        // (SidebarImportMenu) — plus d'entrée séparée ici.
        // Récap Import est désormais dans SidebarImportMenu, Récap Export dans
        // SidebarExportMenu — plus d'entrée séparée ici.
        {
            title: 'Cartographie TC VIDES', path: '/flotte-carte', icon: 'bi-geo-alt',
            visible: hasRole(user, 'MODULE_IMPORT')
        },

        // ── MODULE PARC ───────────────────────────────────────
        // Gestion des Parcs est désormais dans SidebarReglagesMenu — plus d'entrée séparée ici.

        // ── RÉGLAGES ──────────────────────────────────────────
        // Chauffeurs, Camions, Remorques, Clients, Transitaires, Armateurs et Parcs sont
        // regroupés dans SidebarReglagesMenu, rendu plus bas — pas d'entrée séparée ici.

        // ── MODULE CARBURANT ──────────────────────────────────
        // Saisie, Rapports et Paramétrage sont regroupés dans SidebarCarburantMenu, rendu plus
        // bas comme SidebarImportMenu — pas d'entrée séparée ici.

        // ── MODULE PANNES ─────────────────────────────────────
        // Gestion des Pannes et Paramétrage sont regroupés dans SidebarPannesMenu, rendu plus
        // bas comme SidebarCarburantMenu — pas d'entrée séparée ici.

        // ── MODULE DOCUMENTS ──────────────────────────────────
        {
            title: 'Documents Camion', path: '/documents-camion', icon: 'bi-file-earmark-check',
            visible: hasRole(user, 'MODULE_DOCUMENTS', 'MODULE_ADMIN')
        },

        // ── MODULE STATS ──────────────────────────────────────
        {
            title: t('statistiques'), path: '/statistiques', icon: 'bi-bar-chart',
            visible: hasRole(user, 'MODULE_STATS', 'MODULE_ADMIN')
        },
        {
            title: 'Rapports Logistique', path: '/rapports-logistique', icon: 'bi-graph-up-arrow',
            visible: hasRole(user, 'MODULE_STATS', 'MODULE_ADMIN')
        },

        // ── MODULE ADMIN ──────────────────────────────────────
        {
            title: t('gestionUtilisateurs'), path: '/admin/users', icon: 'bi-person-gear',
            visible: hasRole(user, 'MODULE_ADMIN')
        },
    ];

    /**
     * Une entrée est active quand son chemin est le PLUS LONG à correspondre à l'URL courante.
     *
     * Un simple `startsWith` allumait deux entrées à la fois : sur /carburant/rapports, l'entrée
     * « Carburant » (/carburant) correspondait aussi, puisque son chemin en est un préfixe.
     *
     * Un test d'égalité stricte aurait réglé ce cas mais en aurait cassé un autre :
     * /carburant/parametrage et /pannes/parametrage sont de vraies routes SANS entrée de menu
     * dédiée. Sur ces écrans, le parent doit rester surligné, sans quoi plus rien ne le serait.
     *
     * La règle du plus long conserve les deux comportements : le parent s'allume pour ses
     * sous-routes anonymes, et s'efface dès qu'une entrée plus précise existe.
     */
    const cheminsConnus = ['/dashboard', ...menuItems.map(item => item.path)];

    const correspond = (chemin: string) =>
        location.pathname === chemin || location.pathname.startsWith(chemin + '/');

    const isActive = (path: string) => {
        if (!correspond(path)) return false;
        // Écarté si une autre entrée, plus précise, correspond également.
        return !cheminsConnus.some(autre =>
            autre !== path && autre.length > path.length && correspond(autre));
    };

    const sidebarClasses = isMobileView
        ? 'w-full h-auto relative shadow-none pb-0 bg-gradient-to-b from-green-800 to-green-900'
        : `${isCollapsed ? 'w-[64px]' : 'w-[260px]'} h-screen fixed left-0 top-0 z-[1000] flex flex-col overflow-hidden shadow-2xl shadow-black/30 transition-all duration-300 ease-out bg-gradient-to-b from-green-800 via-green-800 to-green-900 border-r border-white/5`;

    return (
        <div className={sidebarClasses}>
            {/* Header avec bouton hamburger */}
            <div
                className={`${isMobileView ? 'p-3' : (isCollapsed ? 'py-4 px-2' : 'p-4')} flex ${isCollapsed && !isMobileView ? 'flex-col items-center gap-3' : 'items-center justify-between'} ${isMobileView ? 'flex-wrap gap-2.5' : ''}`}>
                {/* Logo Section — sert aussi de lien vers le Dashboard (pas d'entrée dédiée dans le menu) */}
                <Link to="/dashboard" title="Dashboard"
                      className={`no-underline group ${isCollapsed && !isMobileView ? '' : 'flex-1'}`}>
                    <div
                        className={`flex items-center cursor-pointer ${isCollapsed ? 'justify-center' : 'justify-start'}`}
                    >
                        <div
                            className={`relative flex items-center justify-center rounded-xl backdrop-blur-sm shadow-lg transition-all duration-300 group-hover:ring-emerald-400/40 group-hover:scale-105 ${isCollapsed ? 'w-9 h-9' : 'w-10 h-10 mr-3'} ${
                                isActive('/dashboard')
                                    ? 'bg-emerald-400/25 ring-2 ring-emerald-400/70 shadow-emerald-900/40'
                                    : 'bg-white/10 ring-1 ring-white/10'
                            }`}>
                            <img
                                src={logo}
                                alt="Translon Logo"
                                className="w-6 h-6 object-contain"
                            />
                            {isActive('/dashboard') && (
                                <span
                                    className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.9)]"/>
                            )}
                        </div>
                        {!isCollapsed && (
                            <span
                                className="text-white text-[15px] font-bold tracking-wide transition-opacity duration-300 bg-gradient-to-r from-white to-emerald-200 bg-clip-text text-transparent">
                TRANSLON
              </span>
                        )}
                    </div>
                </Link>

                {/* Bouton hamburger */}
                <button
                    onClick={toggleSidebar}
                    aria-label={isCollapsed ? 'Déplier le menu' : 'Replier le menu'}
                    className={`bg-white/10 border border-white/15 text-white text-base cursor-pointer p-2 rounded-lg transition-all duration-200 hover:bg-white/20 hover:border-emerald-400/40 active:scale-95 ${isCollapsed && !isMobileView ? '' : 'ml-2'}`}
                >
                    <i className="bi bi-list"></i>
                </button>
            </div>

            <div className="mx-4 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent"/>

            {/* Recherche dans le menu — évite de scanner 15+ entrées à l'œil pour retrouver un module */}
            {!isCollapsed && (
                <div className="px-3 pt-1 pb-2">
                    <div className="relative">
                        <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-[13px]"/>
                        <input
                            type="text"
                            value={menuSearch}
                            onChange={(e) => setMenuSearch(e.target.value)}
                            placeholder="Rechercher un module…"
                            aria-label="Rechercher un module du menu"
                            className="w-full bg-white/10 border border-white/10 rounded-lg pl-8 pr-3 py-1.5 text-[12.5px] text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-emerald-400/50 focus:bg-white/15"
                        />
                    </div>
                </div>
            )}

            {/* Navigation Menu */}
            <nav className={`flex-1 overflow-y-auto overflow-x-hidden ${isMobileView ? 'py-2.5' : 'py-4'}`}
                 style={{scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.15) transparent'}}>
                {/* Les sous-menus Import/Export ne sont pas filtrables par le champ ci-dessus
                    (leurs entrées sont dans des composants séparés) — on les masque pendant une
                    recherche active pour ne pas laisser croire qu'ils ont été pris en compte. */}
                {!menuSearch && hasRole(user, 'MODULE_IMPORT', 'MODULE_ADMIN') && (
                    <SidebarImportMenu isCollapsed={isCollapsed}/>
                )}

                {!menuSearch && hasRole(user, 'MODULE_EXPORT', 'MODULE_ADMIN') && (
                    <SidebarExportMenu isCollapsed={isCollapsed}/>
                )}

                {!menuSearch && hasRole(user, 'MODULE_CARBURANT', 'MODULE_ADMIN') && (
                    <SidebarCarburantMenu isCollapsed={isCollapsed}/>
                )}

                {!menuSearch && hasRole(user, 'MODULE_PANNES', 'MODULE_ADMIN') && (
                    <SidebarPannesMenu isCollapsed={isCollapsed}/>
                )}

                {/* MODULE_PARC ouvre aussi ce menu : Gestion des Parcs y a été déplacée, et un
                    utilisateur qui n'a que ce droit doit continuer d'y accéder. */}
                {!menuSearch && hasRole(user, 'MODULE_REGLAGES', 'MODULE_ADMIN', 'MODULE_PARC') && (
                    <SidebarReglagesMenu
                        isCollapsed={isCollapsed}
                        peutVoirParc={hasRole(user, 'MODULE_PARC')}
                        peutVoirReferentiels={hasRole(user, 'MODULE_REGLAGES', 'MODULE_ADMIN')}
                    />
                )}

                {/* Autres menus */}
                {menuItems.filter(item => item.visible && normalize(item.title).includes(normalize(menuSearch))).map((item, index) => {
                    const active = isActive(item.path);
                    const showFlyout = isCollapsed && !isMobileView && hoveredIndex === index;
                    return (
                        <div
                            key={index}
                            className="relative"
                            onMouseEnter={(e) => {
                                if (!isCollapsed) return;
                                const rect = e.currentTarget.getBoundingClientRect();
                                setFlyoutPos({top: rect.top, left: rect.right + 8});
                                setHoveredIndex(index);
                            }}
                            onMouseLeave={() => setHoveredIndex(null)}
                        >
                            {active && !isCollapsed && (
                                <span
                                    className="absolute left-1.5 top-1/2 -translate-y-1/2 w-1 h-6 bg-emerald-400 rounded-full shadow-[0_0_8px_rgba(52,211,153,0.7)]"/>
                            )}
                            <Link
                                to={item.path}
                                className={`group flex items-center no-underline transition-all duration-200 rounded-xl mx-3 my-0.5 ${
                                    active
                                        ? 'bg-gradient-to-r from-emerald-400 to-green-500 text-white shadow-md shadow-emerald-900/30'
                                        : 'text-white/80 hover:bg-white/10 hover:text-white hover:translate-x-0.5'
                                } ${isCollapsed
                                    ? (isMobileView ? 'py-3 justify-center' : 'py-3 justify-center')
                                    : (isMobileView ? 'py-3 px-4' : 'py-2.5 px-4')
                                }`}
                            >
                                <i
                                    className={`${item.icon} text-[17px] ${isCollapsed ? '' : 'mr-3'} w-5 text-center transition-transform duration-200 ${active ? '' : 'group-hover:scale-110'}`}
                                />
                                {!isCollapsed && (
                                    <span
                                        className="text-[13px] font-medium tracking-wide transition-opacity duration-300">
                  {item.title}
                </span>
                                )}
                            </Link>

                            {/* Flyout au survol en mode réduit, rendu via portail (position: fixed)
                                pour échapper à l'overflow-x-hidden du <nav> qui sinon le tronque. */}
                            {showFlyout && flyoutPos && createPortal(
                                <div
                                    style={{position: 'fixed', top: flyoutPos.top, left: flyoutPos.left}}
                                    className="rounded-xl bg-green-900/95 backdrop-blur-sm ring-1 ring-white/10 shadow-2xl shadow-black/40 py-2 px-4 z-[1100] whitespace-nowrap animate-[fadeIn_0.15s_ease-out]">
                                    <span className="text-[12.5px] font-semibold text-white">{item.title}</span>
                                </div>,
                                document.body
                            )}
                        </div>
                    );
                })}
                {menuSearch && menuItems.filter(item => item.visible && normalize(item.title).includes(normalize(menuSearch))).length === 0 && (
                    <p className="px-4 py-3 text-[12.5px] text-white/40">Aucun module ne correspond à
                        « {menuSearch} ».</p>
                )}
            </nav>

            {/* Pied de sidebar — alertes + mini carte utilisateur */}
            {!isMobileView && (
                <div className="px-3 pb-4 pt-2">
                    <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-2"/>
                    <div className="mb-2">
                        <AlertesCloche isCollapsed={isCollapsed}/>
                    </div>
                    <div
                        className={`flex items-center rounded-xl bg-white/5 backdrop-blur-sm ring-1 ring-white/10 transition-all duration-300 ${isCollapsed ? 'justify-center p-2' : 'gap-3 p-2.5'}`}>
                        <div
                            className="w-8 h-8 shrink-0 rounded-full bg-gradient-to-br from-emerald-400 to-green-600 flex items-center justify-center text-white text-xs font-bold shadow-inner">
                            {(user?.nomComplet || user?.username || '?').slice(0, 1).toUpperCase()}
                        </div>
                        {!isCollapsed && (
                            <div className="min-w-0">
                                <p className="text-white text-xs font-semibold truncate">{user?.nomComplet || user?.username || 'Utilisateur'}</p>
                                <p className="text-emerald-200/70 text-[11px] truncate">{user?.role}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Sidebar;
